import React from 'react';
import './DateRangeFilter.css';

/** Properties for the DateRangeFilter component. */
interface DateRangeFilterProps {
  /** The label displayed for the filter. */
  label: string;
  /** The selected 'from' date, or null if unset. */
  fromDate: string | null;
  /** The selected 'to' date, or null if unset. */
  toDate: string | null;
  /** Called when the date range changes. */
  onChange: (from: string | null, to: string | null) => void;
}

/** Format a date string (yyyy-mm-dd) to m/d/yyyy for pill display. */
const formatDateForPill = (dateStr: string): string => {
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
};

/** An inline header date range filter. Click the label to open a popout with From/To date inputs. */
/**
 * A filter component that allows selecting a date range (from/to) for table column headers.
 * Displays selected dates as removable pills.
 */
export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ label, fromDate, toDate, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const [draftFrom, setDraftFrom] = React.useState<string>(fromDate ?? '');
  const [draftTo, setDraftTo] = React.useState<string>(toDate ?? '');
  const ref = React.useRef<HTMLDivElement>(null);

  // Sync draft state when the popout opens
  React.useEffect(() => {
    if (open) {
      setDraftFrom(fromDate ?? '');
      setDraftTo(toDate ?? '');
    }
  }, [open]);

  // Close on click outside
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleApply = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(draftFrom || null, draftTo || null);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null, null);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleRemoveFrom = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null, toDate);
  };

  const handleRemoveTo = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(fromDate, null);
  };

  return (
    <div ref={ref} className="DateRangeFilter" style={{ position: 'relative' }}>
      <div className="DateRangeFilter__Row">
        {/* Clickable header */}
        <span
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          className={`DateRangeFilter__Header ${(fromDate || toDate) ? 'DateRangeFilter__Header--active' : ''}`}
          title="Filter by date range"
        >
          {label}
          <i className="material-icons" style={{ fontSize: 16, verticalAlign: 'middle', marginLeft: 2 }}>
            {open ? 'arrow_drop_up' : 'arrow_drop_down'}
          </i>
        </span>

        {/* Pills inline to the right, stacking downward */}
        {(fromDate || toDate) && (
          <div className="DateRangeFilter__Pills">
            {fromDate && (
              <span className="DateRangeFilter__Pill">
                From: {formatDateForPill(fromDate)}
                <i className="material-icons DateRangeFilter__PillClose" onClick={handleRemoveFrom}>close</i>
              </span>
            )}
            {toDate && (
              <span className="DateRangeFilter__Pill">
                To: {formatDateForPill(toDate)}
                <i className="material-icons DateRangeFilter__PillClose" onClick={handleRemoveTo}>close</i>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Popout */}
      {open && (
        <div className="DateRangeFilter__Popout" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <div className="DateRangeFilter__Field">
            <label>From</label>
            <input
              type="date"
              value={draftFrom}
              onChange={e => setDraftFrom(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="DateRangeFilter__Field">
            <label>To</label>
            <input
              type="date"
              value={draftTo}
              onChange={e => setDraftTo(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="DateRangeFilter__Actions">
            <button className="DateRangeFilter__Clear" onClick={handleClear}>
              Clear
            </button>
            <button className="DateRangeFilter__Apply" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
